import React, { useState, useEffect } from "react";
import { ImPlus, ImMinus } from "react-icons/im";
import { motion, AnimatePresence } from "framer-motion";
import NavTitle from "./NavTitle";
import { useTranslation } from "react-i18next";

const Category = ({ data, onCategoryFilter }) => {
  const { t } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Transform category data into a more structured format
  const [categoryTree, setCategoryTree] = useState([]);

  useEffect(() => {
    if (data && data.categoryList) {
      // Organize categories with potential subcategories
      const organizedCategories = data.categoryList.map(category => ({
        id: category.id,
        name: category.catName,
        subcategories: category.subcategories || [], // Add support for nested categories
      }));
      setCategoryTree(organizedCategories);
    }
  }, [data]);

  // Update parent component when selected categories change
  useEffect(() => {
    onCategoryFilter(selectedCategories);
  }, [selectedCategories, onCategoryFilter]);

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Handle category selection with support for nested selection
  const handleCategoryToggle = (category, isSubcategory = false) => {
    setSelectedCategories(prev => {
      const categoryName = isSubcategory ? category.parentName + ' - ' + category.name : category.name;

      if (prev.includes(categoryName)) {
        // Deselect category
        return prev.filter(cat => cat !== categoryName);
      } else {
        // Select category
        return [...prev, categoryName];
      }
    });
  };

  // Animation variants
  const categoryVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: { opacity: 1, height: "auto" },
  };

  return (
    <div className="w-full bg-white shadow-lg rounded-xl p-5 transition-all duration-300 hover:shadow-xl">
      <NavTitle
        title={t("shop.Shop_by_Category")}
        icons={false}
        className="mb-4 text-lg font-semibold"
      />

      <div className="space-y-3">
        {categoryTree.map((category) => (
          <motion.div
            key={category.id}
            className="border-b border-gray-100 last:border-b-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className={`flex items-center justify-between cursor-pointer p-3 rounded-lg transition-all duration-200
                ${selectedCategories.includes(category.name)
                  ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-primeColor shadow-sm'
                  : 'hover:bg-gray-50 text-gray-700'}`}
              onClick={() => {
                handleCategoryToggle(category);
                category.subcategories.length > 0 && toggleCategoryExpansion(category.id);
              }}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 border-2 rounded transition-all duration-200 flex items-center justify-center
                  ${selectedCategories.includes(category.name)
                    ? 'border-primeColor bg-primeColor'
                    : 'border-gray-300'}`}
                >
                  {selectedCategories.includes(category.name) && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-2 h-2 bg-white rounded-sm"
                    />
                  )}
                </div>
                <span className="font-medium">{category.name}</span>
              </div>

              {category.subcategories.length > 0 && (
                <motion.div
                  animate={{ rotate: expandedCategories[category.id] ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ImPlus className={`transform transition-all duration-200 
                    ${expandedCategories[category.id] ? 'rotate-45' : ''}`}
                  />
                </motion.div>
              )}
            </div>

            <AnimatePresence>
              {category.subcategories.length > 0 && expandedCategories[category.id] && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={categoryVariants}
                  transition={{ duration: 0.2 }}
                  className="pl-8 mt-2 space-y-2"
                >
                  {category.subcategories.map((subcategory) => (
                    <motion.div
                      key={subcategory.id}
                      initial={{ x: -10, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-all duration-200
                        ${selectedCategories.includes(`${category.name} - ${subcategory.name}`)
                          ? 'bg-blue-50 text-primeColor'
                          : 'hover:bg-gray-50 text-gray-600'}`}
                      onClick={() => handleCategoryToggle({
                        ...subcategory,
                        parentName: category.name
                      }, true)}
                    >
                      <div className={`w-3 h-3 border-2 rounded-sm transition-all duration-200
                        ${selectedCategories.includes(`${category.name} - ${subcategory.name}`)
                          ? 'border-primeColor bg-primeColor'
                          : 'border-gray-300'}`}
                      />
                      <span className="text-sm">{subcategory.name}</span>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {selectedCategories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="mt-6 bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl"
          >
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              {t("shop.Selected_Categories")} ({selectedCategories.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {selectedCategories.map(category => (
                <motion.span
                  key={category}
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  className="bg-white text-primeColor text-xs px-3 py-1.5 rounded-full 
                    flex items-center shadow-sm border border-blue-100"
                >
                  {category}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryToggle({ name: category });
                    }}
                    className="ml-2 hover:text-red-500 transition-colors duration-200"
                  >
                    ×
                  </button>
                </motion.span>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Category;